import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { SpeedInsights } from '@vercel/speed-insights/react'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import { initSentry } from './lib/sentry'
import { maybeInitDebugConsole } from './lib/debugConsole'
import { installGlobalErrorLogging } from './lib/debugLog'
import { handlePreloadError } from './lib/preloadReloadGuard'
// SPEC-LOGIN-BOUNCE-AFTER-SUCCESS-01 (D-838): ページが読み込まれた理由(再読込か/SW か)を残す。
//   ★候補 2(chunk 失敗で再読込)/候補 3(SW の controllerchange で再読込)を実データで切り分けるため。
import { traceAuth, TRACE } from './lib/authTrace'
import { defaultWaitForController } from './lib/versionReload'
import { hydrateFeatureFlags, refreshFeatureFlags, startFeatureFlagsSync } from './lib/featureFlags'
import './index.css'

// OTA gen-3
initSentry()

// SPEC-DEBUG-LOGS-WIRING-AND-CRASH-RESEARCH-01 (D-092) A2: window.onerror / unhandledrejection を
// debug_logs へ (develop preview のみ、内部 gate 済)。React 到達前のエラーも拾う。
installGlobalErrorLogging()

// SPEC-DEBUG-LOGS-WIRING-AND-CRASH-RESEARCH-01 (D-092) quick-win / SPEC-PWA-SW-AUTOUPDATE-KILL-RELOAD-LOOP-01 (D-095):
// Vite は dynamic import 失敗 (旧 chunk = Failed to fetch dynamically imported module) 時に window へ
// 'vite:preloadError' を発火する (https://vite.dev/guide/build.html#load-error-handling)。
// D-092 の生 reload 直呼び (1回フラグのみ) は iOS PWA の sessionStorage 揮発ですり抜け無限reload の一因(穴2)だった。
// D-095 で handlePreloadError に置換: リトライ上限(3) + controllerchange 待ち。上限超過は reload せず warn のみ。
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', () => {
    handlePreloadError({ waitForController: defaultWaitForController })
  })
}

// SPEC-DEBUG-ERUDA-INSCREEN-CONSOLE-01 (D-072): gate 付き動的 import (?debug=1 / __DEBUG__ / localStorage np_debug=1)。
// static import しないので通常ユーザーの bundle には乗らない。eruda 起動時に __NUMPAD_LOG__ も自動 ON。
maybeInitDebugConsole()

// SPEC-SW-UPDATE-TRIGGER-01: side-effect import, registerSW()を実行する唯一の経路。
// SPEC-PWA-SW-STRIP-PHASE1-01でmain.jsxの `export { updateSW } from './lib/swRegistration'`
// を「未使用の再exportで死んでいる」と誤判定し削除したが、それがswRegistration.js自体を
// importする唯一の経路だったため、以降 registerSW() が一切実行されず新規インストールで
// SW登録自体が発生しない状態だった (git show 402e1c2 で確認)。
import './lib/swRegistration'

// SPEC-OCR-STAGED-PROD-RELEASE-V1 (D-427) ITEM_2: 実行時フィーチャーフラグの起動ロード。
//   app state から hydrate(初期値)→ supabase から refresh。可視化時にも refresh(60秒スロットル)。
//   取得失敗時は featureFlags 側が既知値を保持するフェイルセーフ。段階OCR の全社一斉スイッチ/キルスイッチの土台。
if (typeof window !== 'undefined') {
  hydrateFeatureFlags().then(() => refreshFeatureFlags()).catch(() => {})
  let lastFlagRefresh = 0
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    const now = Date.now()
    if (now - lastFlagRefresh < 60000) return
    lastFlagRefresh = now
    refreshFeatureFlags().catch(() => {})
  })
  // SPEC-FEATURE-FLAG-REACHABILITY-V1 (D-428): 認証確立での再取得(ITEM_1)+ 前面5分ポーリング(ITEM_2、キルスイッチ
  //   到達上限)+ 起動直後の短期リトライ(ITEM_3)。背面復帰なしでフラグが届く。1回だけ起動。
  startFeatureFlagsSync()
}

// ★D-838: 起動の理由を 1 行だけ残す。navigation type は reload / navigate / back_forward。
try {
  const nav = performance?.getEntriesByType?.('navigation')?.[0]
  traceAuth(TRACE.pageLoad, String(nav?.type || 'unknown').replace(/[^a-z_]/g, ''), {})
} catch { /* 計測は起動を止めない */ }

ReactDOM.createRoot(document.getElementById('root')).render(
  <BrowserRouter>
    <ErrorBoundary>
      <App />
      {/* SPEC-UPDATE-ENTRY-SINGLE-POINT-V1 (D-463) ITEM_1: 常駐更新バナーを撤去。更新の入口はログイン画面の
          版パネル1つに集約(通常画面では更新告知を出さない=ひろ決定。ランチャーのカード店名被りも解消)。 */}
      <SpeedInsights />
    </ErrorBoundary>
  </BrowserRouter>
)
